import React, { useEffect, useMemo, useState } from "react";
import { EntityReference, FieldProps, GeoPoint, useDataSource } from "@firecms/core";
import { Autocomplete, AutocompleteItem, Button, TextField } from "@firecms/ui";
import { locationsCollection, LOCATIONS_PATH, LOCATIONS_FETCH_LIMIT } from "../collections/locations";

// Max entries rendered in the picker dropdown at once; the full list is
// searchable, this only caps what is on screen
const PICKER_MAX_VISIBLE = 50;

export type DirectoryLocationValue = {
    ref: EntityReference;
    name: string;
    latlng: GeoPoint;
    range: number;
    // DR-447: city | state | country — drives the astro query strategy
    // (GEO_DISTANCE for cities, exact string match for states/countries)
    type?: string;
};

/**
 * Custom field for filter_fields.location (DR-408).
 * Attaches a location from setting/directory/locations to a directory page,
 * denormalising { ref, name, latlng, range, type } onto the directory
 * document. `range` pre-fills from the location's default_range_miles and
 * stays overridable per directory. Handles legacy string values gracefully.
 */
export function LocationAttachmentField({
    property,
    value,
    setValue
}: FieldProps<any>) {
    const dataSource = useDataSource();
    const [locations, setLocations] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [pickerOpen, setPickerOpen] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        dataSource.fetchCollection({
            path: LOCATIONS_PATH,
            collection: locationsCollection as any,
            limit: LOCATIONS_FETCH_LIMIT,
            orderBy: "name",
            order: "asc"
        }).then((result) => {
            if (active) {
                setLocations(result.filter((e: any) => e.values?.enabled !== false));
                setLoading(false);
            }
        }).catch((err) => {
            console.error("Error fetching directory locations:", err);
            if (active) setLoading(false);
        });
        return () => {
            active = false;
        };
    }, [dataSource]);

    const pickLocation = (locationId: string) => {
        const entity = locations.find((l) => l.id === locationId);
        if (!entity) return;
        const values = entity.values || {};
        setValue({
            ref: new EntityReference(entity.id, LOCATIONS_PATH),
            name: values.name || "",
            latlng: values.latlng,
            range: typeof values.default_range_miles === "number" ? values.default_range_miles : 0,
            // DR-447: denormalise the location type — the frontend never
            // dereferences `ref`, so the type must travel with the map
            ...(values.type ? { type: values.type } : {})
        });
    };

    const filteredLocations = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return locations;
        return locations.filter((l) =>
            String(l.values?.name || "").toLowerCase().includes(q)
            || String(l.id).toLowerCase().includes(q));
    }, [locations, query]);

    const visibleLocations = filteredLocations.slice(0, PICKER_MAX_VISIBLE);

    const picker = (
        <div className="relative">
            <TextField
                value={query}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setPickerOpen(true);
                }}
                onClick={() => setPickerOpen(true)}
                label={property.name}
                placeholder={loading ? "Loading locations..." : "Search locations by name or slug..."}
                size="small"
                disabled={loading}
            />
            <Autocomplete open={pickerOpen && !loading} setOpen={setPickerOpen}>
                {visibleLocations.map((l) => (
                    <AutocompleteItem
                        key={l.id}
                        onClick={() => {
                            pickLocation(l.id);
                            setPickerOpen(false);
                            setQuery("");
                        }}
                    >
                        <span className="text-sm">
                            {l.values?.name || l.id}{" "}
                            <span className="font-mono text-xs text-text-secondary dark:text-text-secondary-dark">
                                ({l.id})
                            </span>
                        </span>
                    </AutocompleteItem>
                ))}
                {visibleLocations.length === 0 && (
                    <div className="p-4 text-sm text-text-secondary dark:text-text-secondary-dark">
                        No locations match "{query}"
                    </div>
                )}
                {filteredLocations.length > PICKER_MAX_VISIBLE && (
                    <div className="px-4 py-2 text-xs text-text-secondary dark:text-text-secondary-dark">
                        {filteredLocations.length - PICKER_MAX_VISIBLE} more — keep typing to narrow down
                    </div>
                )}
            </Autocomplete>
        </div>
    );

    // Legacy shape: a plain string slug written before DR-408
    if (typeof value === "string" && value) {
        return (
            <div className="flex flex-col gap-3 p-4 border border-amber-300 dark:border-amber-700 rounded-md bg-amber-50 dark:bg-amber-950/30">
                <span className="text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                    {property.name}
                </span>
                <div className="flex justify-between items-center gap-4">
                    <span className="text-sm text-text-primary dark:text-text-primary-dark">
                        Legacy location value: <span className="font-mono">"{value}"</span>. Select a location below to upgrade.
                    </span>
                    <Button variant="outlined" size="small" onClick={() => setValue(null)}>
                        Clear
                    </Button>
                </div>
                {picker}
            </div>
        );
    }

    // New shape: { ref, name, latlng, range }
    if (value && typeof value === "object" && value.ref) {
        const latlng = value.latlng;
        const coords = latlng && typeof latlng.latitude === "number"
            ? `${latlng.latitude.toFixed(4)}, ${latlng.longitude.toFixed(4)}`
            : "—";
        return (
            <div className="flex flex-col gap-3 p-4 border border-surface-200 dark:border-surface-800 rounded-md bg-surface-50 dark:bg-surface-900">
                <span className="text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                    {property.name}
                </span>
                <div className="flex justify-between items-center gap-4">
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-text-primary dark:text-text-primary-dark">
                            {value.name || "Unnamed location"}
                        </span>
                        <span className="text-xs font-mono text-text-secondary dark:text-text-secondary-dark mt-0.5">
                            {coords} · ref: {value.ref?.id} · type: {value.type || "—"}
                        </span>
                        {!value.type && (
                            <span className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                                No location type — re-attach the location after it has a type (DR-447)
                            </span>
                        )}
                    </div>
                    <Button variant="outlined" size="small" onClick={() => setValue(null)}>
                        Clear
                    </Button>
                </div>
                <TextField
                    value={typeof value.range === "number" ? value.range : ""}
                    onChange={(e) => {
                        const parsed = parseFloat(e.target.value);
                        setValue({
                            ...value,
                            range: Number.isNaN(parsed) ? null : parsed
                        });
                    }}
                    label="Range (miles)"
                    size="small"
                    type="number"
                />
                {(typeof value.range !== "number" || value.range <= 0) && (
                    <span className="text-xs text-red-500">Range must be a positive number of miles</span>
                )}
            </div>
        );
    }

    return picker;
}
